import { Button } from '@components/Button'
import * as Sentry from '@sentry/react-native'
import { ErrorBoundaryProps } from 'expo-router'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Platform } from 'react-native'
import { TamaguiProvider, Text, YStack } from 'tamagui'

import config from '../theme'

/**
 * Último anteparo do app: qualquer erro de render que suba até a raiz cai aqui
 * em vez de desmontar a árvore React e deixar a tela em branco.
 *
 * O expo-router aplica este componente lendo o export `ErrorBoundary` do
 * `app/_layout.tsx`, e o wrapper fica *acima* dele — ou seja, fora de todos os
 * providers do app. Como o `ThemeProvider` depende de contexto que pode ser
 * justamente o que quebrou (feature flags via Apollo), o fallback monta o
 * próprio `TamaguiProvider` com o config estático do tema: os componentes e
 * tokens padrão funcionam sem depender de mais nada.
 */
export function RootErrorBoundary({ error, retry }: ErrorBoundaryProps) {
  const { t } = useTranslation('ErrorBoundary')

  useEffect(() => {
    Sentry.captureException(error, { tags: { boundary: 'root' } })
  }, [error])

  const handleRetry = () => {
    // Na web o reload é mais eficaz que remontar a rota: derruba estado sujo e
    // ainda pega um bundle novo se o deploy tiver avançado.
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      window.location.reload()
      return
    }

    retry()
  }

  return (
    <TamaguiProvider config={config} defaultTheme="dark">
      <YStack
        flex={1}
        ai="center"
        jc="center"
        bg="$bg-page"
        gap="$spacing-inset-sm"
        paddingHorizontal="$spacing-inset-sm"
        testID="root-error-boundary"
      >
        <YStack gap="$spacing-inset-nano" maxWidth={420}>
          <Text
            ff="$mulishBold"
            fontSize="$font-size-md"
            fontWeight="700"
            color="$text-primary"
            textAlign="center"
          >
            {t('title')}
          </Text>
          <Text
            ff="$mulishRegular"
            fontSize="$font-size-xs"
            color="$text-primary"
            textAlign="center"
          >
            {t('description')}
          </Text>
        </YStack>

        <Button
          accessibilityLabel={t('retry')}
          onPress={handleRetry}
          testID="root-error-boundary-retry"
        >
          {t('retry')}
        </Button>
      </YStack>
    </TamaguiProvider>
  )
}
